import { genealogyCarriers, getGenealogyTreeExportUrl, useGenealogyLoci, useGenealogyTree } from "@api/generated/genealogy/genealogy";
import type { Ancestor, Carrier, GenealogyLociMode } from "@api/generated/model";
import { GenotypeSearch, LineLink } from "@features/genealogy/GenotypeParts";
import { geneFlows, keyParam, keyText, lineTitle, PEDIGREE_DEPTH, PEDIGREE_MAX_DEPTH } from "@features/genealogy/genotype";
import { TreeView, usePrinting } from "@features/genealogy/TreeView";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/genealogy/genealogy.css";
import { listParam } from "@app/search";
import { Button, Input, Loader, MultiSelect, NumberInput, SegmentedControl } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { Splitter } from "@ui/Splitter";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const MODES: GenealogyLociMode[] = ["locus", "allele"];

export const Route = createFileRoute("/genealogy/tree")({
  validateSearch: z.object({
    paz: z.number().int().optional(),
    sid: z.number().int().optional(),
    depth: z.number().int().min(1).max(PEDIGREE_MAX_DEPTH).optional().catch(undefined),
    genes: listParam().optional().catch(undefined),
    // the lines walked up from, oldest first ("paz/sid/name"): the way back down to them
    trail: listParam().optional().catch(undefined),
    by: z
      .enum(MODES as [GenealogyLociMode, ...GenealogyLociMode[]])
      .optional()
      .catch(undefined), // genes or alleles
  }),
  component: TreePage,
});

/** Genealógia → Pedigréfa (the legacy Pedigree Tree and Analyze one Pedigree): the tree of a line from its recorded
 *  crosses and pedigree text, its ancestors' shares, and which of the chosen genes each node carries. */
function TreePage() {
  const { t } = useTranslation();
  const { paz, sid } = Route.useSearch();
  if (paz == null || sid == null) {
    return (
      <div className="page">
        <PageHeader title={t("tree.title")} subtitle={t("tree.subtitle")} />
        <GenotypeSearch to="/genealogy/tree" />
      </div>
    );
  }
  return <Tree paz={paz} sid={sid} />;
}

function Tree({ paz, sid }: { paz: number; sid: number }) {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const depth = search.depth ?? PEDIGREE_DEPTH;
  const genes = search.genes ?? [];
  const trail = (search.trail ?? []).map(trailStep).filter((s): s is TrailStep => s != null);
  const by = search.by ?? "locus";
  const [mode, setMode] = useState<"all" | "last">("all");
  const { printing, print } = usePrinting();
  const loci = useGenealogyLoci({ mode: by }, { query: { staleTime: Number.POSITIVE_INFINITY } }).data ?? [];
  // while another tree loads, the one on show stays only if it is the same line's (another depth, under a loading veil)
  // the ends one by one walk the full tree's places on the server: asked for only while that list is shown
  const query = useGenealogyTree(paz, sid, mode === "last" ? { depth, ends: true } : { depth }, {
    query: { placeholderData: (prev) => (prev?.root.key.paz === paz && prev.root.key.sid === sid ? prev : undefined) },
  });
  const tree = query.data;
  const reloading = query.isFetching && query.isPlaceholderData;
  // each line once (a repeated ancestor stands many times), asked again only for another tree
  const keys = useMemo(
    () => [...new Map((tree?.nodes ?? []).flatMap((n) => (n.line ? [[keyParam(n.line.key), n.line.key] as const] : []))).values()],
    [tree],
  );
  const found = useQuery({
    queryKey: ["tree-carriers", paz, sid, depth, tree?.generations, genes, by],
    enabled: genes.length > 0 && keys.length > 0,
    queryFn: ({ signal }) => genealogyCarriers({ keys, genes, mode: by }, { signal }),
    placeholderData: keepPreviousData,
  });
  const [carried, tested] = useMemo(() => {
    const byKey = new Map((found.data ?? []).map((c) => [`${c.key.paz}/${c.key.sid}`, c]));
    const of = (pick: (c: Carrier) => string[]) =>
      new Map(
        (tree?.nodes ?? []).flatMap((n) => {
          const c = n.line && byKey.get(`${n.line.key.paz}/${n.line.key.sid}`);
          return n.line ? [[n.id, c ? pick(c) : []] as const] : [];
        }),
      );
    return [of((c) => c.genes), of((c) => c.tested)];
  }, [found.data, tree]);
  const flows = useMemo(() => (tree ? geneFlows(tree.nodes, carried, genes) : []), [tree, carried, genes]);
  if (query.isError && !tree) return <QueryFeedback busy={query.isFetching} error={query.error} retry={query.refetch} />;
  if (!tree)
    return (
      <div className="page fill">
        <PageHeader title={t("tree.title")} subtitle={<span className="mono">{`${paz} / ${sid}`}</span>} />
        <div className="card pad hstack pf-loading-page">
          <Loader size="sm" />
          <span className="mu">{t("tree.loading", { depth })}</span>
        </div>
      </div>
    );
  const set = (p: Partial<typeof search>) => void navigate({ search: (s) => ({ ...s, ...p }) });
  const list: Ancestor[] = mode === "all" ? tree.ancestry : tree.last_level;
  const repeated = tree.ancestry.filter((a) => a.paths > 1);
  // the branches that stop before the depth asked: nothing is recorded above them (no cross, no pedigree)
  // (not while a tree of another depth loads: the one on show was asked for its own depth)
  const endsEarly =
    tree.stopped_at || reloading
      ? []
      : [
          ...new Set(
            tree.nodes
              .filter((n) => n.generation > 0 && n.leaf && n.see == null && n.generation < depth)
              .map((n) => n.label ?? t("tree.unknown")),
          ),
        ];
  const rootStep: TrailStep = { paz, sid, name: lineTitle(tree.root) };
  return (
    <div className="page fill">
      <PageHeader
        title={t("tree.title")}
        subtitle={
          <span className="mono">
            {lineTitle(tree.root)}
            {tree.root.pedigree && tree.root.pedigree !== tree.root.name ? ` · ${tree.root.pedigree}` : ""}
          </span>
        }
        item={{ kind: "genotype", title: lineTitle(tree.root), subtitle: keyText(tree.root.key) }}
        actions={
          <div className="hstack" style={{ gap: 10 }}>
            <DepthInput key={depth} depth={depth} onDepth={(d) => set({ depth: d === PEDIGREE_DEPTH ? undefined : d })} />
            <span className="sm mu">{t("tree.generationsFound", { count: tree.generations })}</span>
            <SegmentedControl
              size="xs"
              aria-label={t("tree.markerModeLabel")}
              value={by}
              onChange={(v) => set({ by: v === "locus" ? undefined : (v as GenealogyLociMode), genes: undefined })}
              data={MODES.map((m) => ({ value: m, label: t(`tree.markerMode.${m}`) }))}
            />
            <MultiSelect
              size="xs"
              w={260}
              aria-label={t(`tree.markerMode.${by}`)}
              placeholder={t(`tree.markerMode.${by}`)}
              data={loci}
              value={genes}
              onChange={(v) => set({ genes: v.length ? v : undefined })}
              searchable
              clearable
              maxValues={8}
            />
            <Button size="compact-sm" variant="default" onClick={print}>
              {t("tree.print")}
            </Button>
            <Button size="compact-sm" variant="default" onClick={() => void navigate({ to: "/genealogy/genotype", search: { paz, sid } })}>
              {t("tree.genotypeSheet")}
            </Button>
          </div>
        }
      />
      <div className="fill-row">
        <section className="card grow pf-wrap" aria-busy={reloading}>
          {trail.length > 0 && (
            <nav className="pf-trail sm" aria-label={t("tree.trail")}>
              {trail.map((step, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: one line may stand twice on a way up and back down
                <span key={`${step.paz}/${step.sid}/${i}`} className="hstack" style={{ gap: 6 }}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => set({ paz: step.paz, sid: step.sid, trail: i ? trail.slice(0, i).map(trailText) : undefined })}
                  >
                    {step.name}
                  </button>
                  <span className="mu">›</span>
                </span>
              ))}
              <b aria-current="page">{lineTitle(tree.root)}</b>
            </nav>
          )}
          {reloading && (
            <div className="pf-veil" role="status">
              <Loader size="sm" />
              <span>{t("tree.loading", { depth })}</span>
            </div>
          )}
          <QueryFeedback busy={query.isFetching && !reloading} error={query.error} retry={query.refetch} />
          <QueryFeedback busy={found.isFetching} error={found.error} retry={found.refetch} label={t("async.genes")} />
          <TreeView
            nodes={tree.nodes}
            genes={genes}
            carried={carried}
            tested={tested}
            onRoot={(n) => {
              if (!n.line || (n.line.key.paz === paz && n.line.key.sid === sid)) return;
              set({ paz: n.line.key.paz, sid: n.line.key.sid, trail: [...trail.map(trailText), trailText(rootStep)] });
            }}
            onOpen={(n) => void navigate({ to: "/genealogy/genotype", search: { paz: n.line?.key.paz, sid: n.line?.key.sid } })}
            printing={printing}
            printTitle={`${t("tree.title")} · ${lineTitle(tree.root)} · ${t("tree.generationsFound", { count: tree.generations })}`}
          />
          <p className="sm mu inset">
            {t("tree.help")}
            {genes.length > 0 && ` · ${t("tree.geneLegend")}`}
          </p>
        </section>
        <Splitter cssVar="--pf-side-w" side="end" min={240} max={600} />
        <aside className="card pad stack pf-side">
          <div className="hstack">
            <h2 className="h2 grow">{t("tree.ancestors")}</h2>
            <SegmentedControl
              size="xs"
              value={mode}
              onChange={(v) => setMode(v as "all" | "last")}
              data={[
                { value: "all", label: t("tree.summed") },
                { value: "last", label: t("tree.lastLevel") },
              ]}
            />
            <Button component="a" href={getGenealogyTreeExportUrl(paz, sid, { depth, mode })} size="compact-xs" variant="default">
              {t("tree.excel")}
            </Button>
          </div>
          <span className="sm mu">{mode === "all" ? t("tree.summedHelp") : t("tree.lastLevelHelp")}</span>
          {tree.nodes.length === 1 && (
            <div className="note stack" style={{ gap: 6 }}>
              <span className="sm">
                {tree.root.pedigree ? t("tree.founder", { pedigree: tree.root.pedigree }) : t("tree.founderNothing")}
              </span>
              <Link className="sm" to="/genealogy/register" search={{ paz, sid }}>
                {t("tree.founderLink")}
              </Link>
            </div>
          )}
          <div className="stack" style={{ gap: 4 }}>
            {list.map((a, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: the last level lists an ancestor once per occurrence
              <div key={`${a.ident}-${i}`} className="pf-share">
                <LineLink line={a.line} text={a.label} />
                <span className="pf-bar" style={{ width: `${Math.min(a.share, 100)}%` }} />
                <span className="mono sm">{formatNumber(a.share, 2)} %</span>
              </div>
            ))}
          </div>
          {tree.stopped_at != null && (
            <p className="note sm" style={{ margin: 0 }}>
              {t("tree.stopped", { generation: tree.stopped_at - 1, count: tree.nodes.length })}
            </p>
          )}
          {tree.last_level_cut && mode === "last" && <p className="sm mu">{t("tree.lastLevelCut", { count: list.length })}</p>}
          {endsEarly.length > 0 && (
            <p className="note sm" style={{ margin: 0 }}>
              {t("tree.endsEarly", {
                names: endsEarly.slice(0, 6).join(", ") + (endsEarly.length > 6 ? " …" : ""),
              })}
            </p>
          )}
          {repeated.length > 0 && mode === "all" && (
            <p className="sm mu">{t("tree.repeatedHelp", { names: repeated.map((a) => a.label).join(", ") })}</p>
          )}
          {genes.length > 0 && (
            <>
              <h2 className="h2">{t(`tree.genesInTree.${by}`)}</h2>
              {flows.map((g) => (
                <div key={g.gene} className="stack" style={{ gap: 2 }}>
                  <span className="chip g" style={{ alignSelf: "flex-start" }}>
                    {g.gene}
                  </span>
                  {g.paths.length ? (
                    g.paths.map((p) => (
                      <span key={p.join(">")} className="sm mono">
                        {p.join(" → ")}
                      </span>
                    ))
                  ) : (
                    <span className="sm mu">{t("tree.noGeneCarrier")}</span>
                  )}
                </div>
              ))}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/** The depth of the tree: any number of generations (1–50), applied a moment after typing or the arrows stop; "All"
 *  asks for as many as the server grows (the tree ends where the register does). */
function DepthInput({ depth, onDepth }: { depth: number; onDepth: (d: number) => void }) {
  const { t } = useTranslation();
  // (keyed by the depth: another one from the address, back or a link, starts the field afresh)
  const [typed, setTyped] = useState<number | string>(depth);
  const apply = useDebouncedCallback((v: number | string) => {
    const d = typeof v === "number" ? Math.round(v) : Number.parseInt(v, 10);
    if (Number.isFinite(d) && d >= 1 && d <= PEDIGREE_MAX_DEPTH && d !== depth) onDepth(d);
  }, 400);
  return (
    <span className="hstack" style={{ gap: 6 }}>
      <Input.Label htmlFor="tree-depth" className="sm mu">
        {t("tree.depth")}
      </Input.Label>
      <NumberInput
        id="tree-depth"
        size="xs"
        w={78}
        min={1}
        max={PEDIGREE_MAX_DEPTH}
        clampBehavior="strict"
        allowDecimal={false}
        value={typed}
        onChange={(v) => {
          setTyped(v);
          apply(v);
        }}
      />
      <Button size="compact-xs" variant={depth === PEDIGREE_MAX_DEPTH ? "light" : "default"} onClick={() => onDepth(PEDIGREE_MAX_DEPTH)}>
        {t("tree.allGenerations")}
      </Button>
    </span>
  );
}

type TrailStep = { paz: number; sid: number; name: string };
/** A step of the way up the tree in the address: "paz/sid/name" (the name may hold "/" too). */
const trailText = (s: TrailStep) => `${keyParam(s)}/${s.name}`;
function trailStep(text: string): TrailStep | null {
  const [paz, sid, ...name] = text.split("/");
  const step = { paz: Number(paz), sid: Number(sid), name: name.join("/") || `${paz} / ${sid}` };
  return Number.isInteger(step.paz) && Number.isInteger(step.sid) ? step : null;
}
